/**
 * TanStack Query hooks for the care_day change_request billing-delta preview
 * (#783). Plan (read-only billing-delta preview) and Process (recovery apply) —
 * both calling the generated ChangeRequestsService.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useLingui } from '@lingui/react';

import { ChangeRequestsService } from '@/api/services/ChangeRequestsService';
import { ApiError } from '@/api';
import { workflowKeys } from './useWorkflows';

/**
 * Read-only billing-delta preview for a care_day change_request item: the bounded
 * window, the weekday op, the current → proposed weekdays, and the affected
 * invoices. Surfaces a toast on failure; the drawer renders the resolved data.
 */
export function usePlanCareDay() {
  const { i18n } = useLingui();

  return useMutation({
    mutationFn: (id: string) => ChangeRequestsService.getApiV1ChangeRequestsCareDayPlan(id),
    onError: () => {
      toast.error(
        i18n._({
          id: 'changeRequest.toast.planError',
          message: 'Failed to load the change preview',
        }),
      );
    },
  });
}

/**
 * Recovery apply for an approved care_day item whose automatic execution failed.
 * Invalidates the inbox + the item detail. Surfaces the real per-apply outcome
 * (Process returns HTTP 200 even on a failed apply).
 */
export function useProcessCareDay() {
  const queryClient = useQueryClient();
  const { i18n } = useLingui();

  return useMutation({
    mutationFn: ({ id }: { id: string }) =>
      ChangeRequestsService.postApiV1ChangeRequestsCareDayProcess(id),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: workflowKeys.lists() });
      queryClient.invalidateQueries({ queryKey: workflowKeys.pendingCount() });
      queryClient.invalidateQueries({ queryKey: workflowKeys.stats() });
      queryClient.invalidateQueries({ queryKey: workflowKeys.detail(variables.id) });

      if (data?.status === 'error') {
        // care_day is a contract/weekday-level change — the child-specific
        // "…to any child" copy from the child_data_update flow does not fit here;
        // reuse the generic (already-translated) apply-error message instead.
        toast.error(
          i18n._({ id: 'changeRequest.toast.applyError', message: 'Failed to apply the change request' }),
        );
      } else {
        toast.success(
          i18n._({ id: 'changeRequest.toast.applied', message: 'Change request applied' }),
        );
      }
    },
    onError: (error: Error) => {
      if (error instanceof ApiError && error.status === 409) {
        toast.error(
          i18n._({
            id: 'changeRequest.toast.applyConflict',
            message: 'This change request must be approved before it can be applied',
          }),
        );
        return;
      }
      toast.error(
        i18n._({
          id: 'changeRequest.toast.applyError',
          message: 'Failed to apply the change request',
        }),
      );
    },
  });
}
